import { z } from 'zod'
import { createPageSchema } from '@/utils/common.data.ts'
import type { Filters } from '@/types/filter.types.ts'
import { VehicleTypesSchema } from '@/types/vehicle.types.ts'

// ============================================================================
// Core Vehicle Schemas (from vehicle-details)
// ============================================================================

// Deplaced from section Vehicle Management Schemas (from vehicle-management) below
export const IntegrationPartner = z.enum(['GEOTAB', 'AMPERE', 'ECHOES', 'NONE'])

export const VehicleHealthStatusSchema = z.enum(['HIGH', 'MEDIUM', 'LOW'])

const VehicleEngineType = ['BEV', 'PHEV', 'HEV', 'ICE'] as const

export const VehicleEngineTypeEnum = z.enum(VehicleEngineType)
export const VehicleEngineTypeEnumForm = z
  .string()
  .nonempty('api.message.errors.vehicleValidationFailed.engineType.emptyMessage')
  .refine(val => VehicleEngineType.includes(val as never), {
    message: 'api.message.errors.vehicleValidationFailed.engineType.invalidMessage'
  })

export type VehicleType = z.infer<typeof VehicleTypesSchema>

// Please change localisation if you add/remove values
export const VehicleStatusSchema = z.enum(['AVAILABLE', 'CHARGING', 'DRIVING', 'UNKNOWN'])

export const ChargingStatusSchema = z.object({
  percentage: z.number().min(0.0).max(1.0),
  rangeInKms: z.number().min(0).optional()
})

export const IntegrationSourceSchema = z.object({
  type: IntegrationPartner,
  identifier: z.string().nullish()
})

export const DatedOdometerSchema = z.object({
  date: z.union([z.date(), z.string().transform(value => new Date(value))]),
  value: z.number().int().nonnegative()
})

export const ContractDetailsSchema = z.object({
  startDate: z.union([z.date(), z.string().transform(value => new Date(value))]),
  endDate: z.union([z.date(), z.string().transform(value => new Date(value))]),
  startDateOdometer: z.number().int(),
  currentMileage: z.number().int(),
  annualContractedMileage: z.number().int(),
  totalContractedMileage: z.number().int(),
  projectedAnnualMileage: z.number().int().nullish(),
  projectedTotalMileage: z.number().int().nullish()
})

export const VehicleSchema = z.object({
  id: z.uuid(),
  siteId: z.union([z.string(), z.number()]).transform(it => String(it)),
  name: z.string(),
  vehicleType: VehicleTypesSchema,
  model: z.string(),
  vin: z.string(),
  licensePlate: z.string().nullish(),
  mileage: z.number().min(0).nullish(),
  vehicleHealth: VehicleHealthStatusSchema,
  chargingStatus: ChargingStatusSchema.nullish(),
  vehicleStatus: VehicleStatusSchema,
  engineType: VehicleEngineTypeEnum,
  issueType: z.string().nullish(),
  isKeyless: z.boolean(),
  lastPosition: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
      bearing: z.number().min(0).max(360).optional(),
      timestamp: z.union([z.date(), z.iso.datetime().transform(value => new Date(value))])
    })
    .nullish()
    .transform(value => value ?? undefined),
  integrationSource: IntegrationSourceSchema,
  depot: z
    .object({
      latitude: z.number(),
      longitude: z.number()
    })
    .optional(),
  contract: ContractDetailsSchema.nullish(),
  acquisitionOdometer: DatedOdometerSchema
})

// ============================================================================
// Vehicle History Schemas
// ============================================================================

// ISO instant accepted either as a Date or an ISO-8601 string.
const instant = z.union([z.date(), z.iso.datetime().transform(value => new Date(value))])

// Single time bucket of the odometer history endpoint
export const OdometerBucketSchema = z.object({
  bucket: instant,
  increase: z.coerce.number()
})
export const OdometerHistorySchema = z.array(OdometerBucketSchema)

// Single time bucket of the state-of-charge history endpoint. Gauge fields are
// null when the bucket holds no readings.
export const StateOfChargeBucketSchema = z.object({
  bucket: instant,
  avg: z.coerce.number().nullable(),
  min: z.coerce.number().nullable(),
  max: z.coerce.number().nullable()
})
export const StateOfChargeHistorySchema = z.array(StateOfChargeBucketSchema)

// Single time bucket of the efficiency history endpoint. `efficiency` is null
// when the bucket holds no readings.
export const EfficiencyBucketSchema = z.object({
  bucket: instant,
  efficiency: z.coerce.number().nullable()
})
export const EfficiencyHistorySchema = z.array(EfficiencyBucketSchema)

// ============================================================================
// Vehicle Management Schemas (from vehicle-management)
// ============================================================================

const VehicleContractDisabledSchema = z.object({ enabled: z.literal(false) })

export const VehicleContractEnabledSchema = z.object({
  enabled: z.literal(true),
  startDate: z.date(),
  endDate: z.date(),
  contractedMileage: z.coerce.number<number>().positive(),
  startDateOdometer: z.coerce.number<number>().min(0)
})

export const VehicleContractFormSchema = z.discriminatedUnion('enabled', [
  VehicleContractDisabledSchema,
  VehicleContractEnabledSchema
])

//TODO add missing translations here
// Form schema for vehicle input validation
export const CreateVehicleFormSchema = z.object({
  // Site Information
  siteId: z.string('api.message.errors.vehicleValidationFailed.site.emptyMessage'),

  // Vehicle Information
  vin: z
    .string('api.message.errors.vehicleValidationFailed.vin.emptyMessage')
    .min(17, 'api.message.errors.vehicleValidationFailed.vin.invalidMessage')
    .max(17, 'api.message.errors.vehicleValidationFailed.vin.invalidMessage')
    .regex(
      /^[A-HJ-NPR-Z0-9]{17}$/,
      'api.message.errors.vehicleValidationFailed.vin.invalidMessage'
    ),
  licensePlate: z
    .string('api.message.errors.vehicleValidationFailed.licensePlate.emptyMessage')
    .min(1, 'api.message.errors.vehicleValidationFailed.licensePlate.emptyMessage'),
  vehicleName: z
    .string('api.message.errors.vehicleValidationFailed.vehicleName.emptyMessage')
    .min(1, 'api.message.errors.vehicleValidationFailed.vehicleName.emptyMessage'),
  vehicleType: VehicleTypesSchema,
  model: z.string().optional(),
  engineType: VehicleEngineTypeEnumForm,

  // Integration Information (Data Provider Source)
  integrationPartner: IntegrationPartner,
  integrationId: z.string().optional().nullable(),

  contract: VehicleContractFormSchema
})

// Vehicle list schemas
export const VehiclePageSchema = createPageSchema(VehicleSchema)

// ============================================================================
// Types
// ============================================================================

export type VehicleHealthStatus = z.infer<typeof VehicleHealthStatusSchema>
export type VehicleStatus = z.infer<typeof VehicleStatusSchema>
export type VehicleEngineType = z.infer<typeof VehicleEngineTypeEnum>
export type Vehicle = z.infer<typeof VehicleSchema>
export type ContractDetails = z.infer<typeof ContractDetailsSchema>
export type OdometerBucket = z.infer<typeof OdometerBucketSchema>
export type OdometerHistory = z.infer<typeof OdometerHistorySchema>
export type StateOfChargeBucket = z.infer<typeof StateOfChargeBucketSchema>
export type StateOfChargeHistory = z.infer<typeof StateOfChargeHistorySchema>
export type DatedOdometer = z.infer<typeof DatedOdometerSchema>
export type EfficiencyBucket = z.infer<typeof EfficiencyBucketSchema>
export type EfficiencyHistory = z.infer<typeof EfficiencyHistorySchema>

export type VehicleContractForm = z.infer<typeof VehicleContractFormSchema>
export type CreateVehicleForm = z.infer<typeof CreateVehicleFormSchema>
export type VehiclePage = z.infer<typeof VehiclePageSchema>

export interface VehicleListFilters extends Filters {
  types?: VehicleType[]
  statuses?: VehicleStatus[]
  engineTypes?: VehicleEngineType[]
  query?: string
  siteIds?: string[]
}
